import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRotateLeft,
  faCheck,
  faCloudArrowUp,
  faDatabase,
  faDownload,
  faEye,
  faFileCode,
  faPen,
  faSearch,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import api, { getData } from "../services/api";
import { useToast } from "../context/ToastContext";
import { fileSize, formatDate } from "../utils/format";
import {
  Button,
  ConfirmModal,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  PageHeader,
  Pagination,
  Skeleton,
  StatusBadge,
} from "../components/UI";

const tabs = [
  "Overview",
  "Upload JSON",
  "Records",
  "Categories",
  "Versions",
  "Import History",
];
export default function KnowledgePage() {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fileRef = useRef();
  const [activeTab, setActiveTab] = useState("Overview");
  const [site, setSite] = useState(null);
  const [stats, setStats] = useState(null);
  const [records, setRecords] = useState({ items: [], pagination: {} });
  const [versions, setVersions] = useState([]);
  const [query, setQuery] = useState({
    search: "",
    category: "",
    active: "",
    page: 1,
    limit: 20,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [siteRes, statsRes, versionRes] = await Promise.all([
        api.get(`/websites/${websiteId}`),
        api.get(`/websites/${websiteId}/knowledge/stats`),
        api.get(`/websites/${websiteId}/knowledge/versions`),
      ]);
      setSite(getData(siteRes).website);
      setStats(getData(statsRes));
      setVersions(getData(versionRes).items);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [websiteId]);
  const loadRecords = useCallback(async () => {
    try {
      const response = await api.get(
        `/websites/${websiteId}/knowledge/records`,
        { params: query },
      );
      setRecords(getData(response));
      setSelected([]);
    } catch (e) {
      setError(e.message);
    }
  }, [websiteId, query]);
  useEffect(() => {
    loadOverview();
  }, [loadOverview]);
  useEffect(() => {
    const timer = setTimeout(loadRecords, 250);
    return () => clearTimeout(timer);
  }, [loadRecords]);
  const chooseFile = (candidate) => {
    setUploadResult(null);
    if (!candidate) return;
    if (!candidate.name.toLowerCase().endsWith(".json"))
      return toast.show("Choose a .json file.", "error");
    if (candidate.size > 5 * 1024 * 1024)
      return toast.show("JSON uploads must be 5 MB or smaller.", "error");
    const reader = new FileReader();
    reader.onload = () => {
      try {
        JSON.parse(reader.result);
        setFile(candidate);
      } catch {
        setFile(null);
        toast.show("This file is not valid JSON.", "error");
      }
    };
    reader.readAsText(candidate);
  };
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setProgress(1);
    const form = new FormData();
    form.append("file", file);
    try {
      const data = getData(
        await api.post(`/websites/${websiteId}/knowledge/upload`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (event) =>
            setProgress(
              event.total ? Math.round((event.loaded * 100) / event.total) : 50,
            ),
        }),
      );
      setProgress(100);
      setUploadResult(data);
      toast.show(
        `Imported ${data.recordCount} records as version ${data.version}.`,
      );
      setFile(null);
      await loadOverview();
      await loadRecords();
    } catch (e) {
      toast.show(e.message, "error");
      setProgress(0);
    } finally {
      setBusy(false);
    }
  };
  const saveRecord = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(
        `/websites/${websiteId}/knowledge/records/${editing._id}`,
        editing,
      );
      toast.show("Knowledge record updated.");
      setEditing(null);
      loadRecords();
      loadOverview();
    } catch (err) {
      toast.show(err.message, "error");
    } finally {
      setBusy(false);
    }
  };
  const executeConfirm = async () => {
    setBusy(true);
    try {
      if (confirm.type === "bulk") {
        const data = getData(
          await api.post(`/websites/${websiteId}/knowledge/bulk-delete`, {
            ids: selected,
          }),
        );
        toast.show(`${data.deletedCount} records deleted.`);
      } else if (confirm.type === "delete") {
        await api.delete(
          `/websites/${websiteId}/knowledge/records/${confirm.record._id}`,
        );
        toast.show("Record deleted.");
      } else {
        await api.post(
          `/websites/${websiteId}/knowledge/versions/${confirm.version}/rollback`,
        );
        toast.show(`Rolled back to version ${confirm.version}.`);
      }
      setConfirm(null);
      loadOverview();
      loadRecords();
    } catch (e) {
      toast.show(e.message, "error");
    } finally {
      setBusy(false);
    }
  };
  const toggle = async (record) => {
    try {
      await api.patch(
        `/websites/${websiteId}/knowledge/records/${record._id}`,
        { isActive: !record.isActive },
      );
      toast.show("Record status updated.");
      loadRecords();
      loadOverview();
    } catch (e) {
      toast.show(e.message, "error");
    }
  };
  const exportData = async () => {
    try {
      const response = await api.get(
        `/websites/${websiteId}/knowledge/export`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${site?.domain || "knowledge"}-v${stats?.currentVersion || 0}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.show("Knowledge export downloaded.");
    } catch (e) {
      toast.show(e.message, "error");
    }
  };
  const categoryRows = useMemo(
    () =>
      (stats?.categories || []).map((category) => ({
        category,
        count: records.items.filter((r) => r.category === category).length,
      })),
    [stats, records],
  );
  if (loading) return <Skeleton rows={8} />;
  if (error && !site)
    return <ErrorState message={error} onRetry={loadOverview} />;
  return (
    <>
      <PageHeader
        eyebrow="Knowledge manager"
        title={site?.name || "Website knowledge"}
        description={`Versioned, searchable information scoped exclusively to ${site?.domain || "this website"}.`}
        actions={
          <>
            <Button
              variant="secondary"
              icon={faDownload}
              onClick={exportData}
              disabled={!stats?.totalRecords}
            >
              Export
            </Button>
            <Button
              icon={faCloudArrowUp}
              onClick={() => setActiveTab("Upload JSON")}
            >
              Upload JSON
            </Button>
          </>
        }
      />
      <nav className="tabs" aria-label="Knowledge sections">
        {tabs.map((tab) => (
          <button
            className={activeTab === tab ? "active" : ""}
            key={tab}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </nav>
      {error && <ErrorState message={error} onRetry={loadOverview} />}
      {activeTab === "Overview" && (
        <>
          <section className="kpi-grid knowledge-kpis">
            {[
              ["Total records", stats.totalRecords],
              ["Active records", stats.activeRecords],
              ["Categories", stats.categories.length],
              ["Current version", `v${stats.currentVersion}`],
            ].map(([label, value], i) => (
              <article className="kpi-card" key={label}>
                <span className={`signal-rail rail-${i}`} />
                <span>{label}</span>
                <strong>{value}</strong>
              </article>
            ))}
          </section>
          <section className="knowledge-overview-grid">
            <article className="panel">
              <span className="eyebrow">Latest import</span>
              <h2>{stats.lastUpload?.filename || "No knowledge imported"}</h2>
              {stats.lastUpload ? (
                <dl className="stacked-details">
                  <div>
                    <dt>Uploaded</dt>
                    <dd>{formatDate(stats.lastUpload.uploadedAt)}</dd>
                  </div>
                  <div>
                    <dt>Records</dt>
                    <dd>{stats.lastUpload.recordCount}</dd>
                  </div>
                  <div>
                    <dt>Uploader</dt>
                    <dd>
                      {stats.lastUpload.uploadedBy?.name || "Administrator"}
                    </dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>
                      <StatusBadge
                        active={stats.lastUpload.status !== "failed"}
                      >
                        {stats.lastUpload.status}
                      </StatusBadge>
                    </dd>
                  </div>
                </dl>
              ) : (
                <p>
                  Upload structured JSON to create the first searchable version.
                </p>
              )}
            </article>
            <article className="panel knowledge-guidance">
              <FontAwesomeIcon icon={faDatabase} />
              <div>
                <span className="eyebrow">Isolation guarantee</span>
                <h2>One tenant, one retrieval boundary.</h2>
                <p>
                  Every record and version here is tied to this website ID.
                  Public searches are also checked against the visitor’s
                  registered domain.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/websites/${websiteId}`)}
                >
                  View installation
                </Button>
              </div>
            </article>
          </section>
        </>
      )}
      {activeTab === "Upload JSON" && (
        <section className="panel upload-panel">
          <div className="upload-copy">
            <span className="eyebrow">Create a new version</span>
            <h2>Import structured website knowledge</h2>
            <p>
              Upload a valid JSON document. Nested objects and arrays are
              normalized into individual searchable records; previous versions
              remain available.
            </p>
            <ul>
              <li>JSON format only</li>
              <li>Maximum file size: 5 MB</li>
              <li>
                Potentially dangerous keys and malformed structures are rejected
              </li>
            </ul>
          </div>
          <div
            className={`dropzone ${dragging ? "is-dragging" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              chooseFile(e.dataTransfer.files[0]);
            }}
          >
            <FontAwesomeIcon icon={faFileCode} />
            <h3>{file ? file.name : "Drop your JSON file here"}</h3>
            <p>
              {file
                ? `${fileSize(file.size)} · Ready to validate and import`
                : "or choose a file from your device"}
            </p>
            <input
              ref={fileRef}
              hidden
              type="file"
              accept=".json,application/json"
              onChange={(e) => chooseFile(e.target.files[0])}
            />
            <div className="dropzone-actions">
              <Button
                variant="secondary"
                onClick={() => fileRef.current.click()}
              >
                Choose file
              </Button>
              {file && (
                <Button onClick={upload} disabled={busy}>
                  {busy ? "Uploading…" : "Upload new version"}
                </Button>
              )}
            </div>
            {progress > 0 && (
              <div className="progress-wrap">
                <div className="progress-bar">
                  <span style={{ width: `${progress}%` }} />
                </div>
                <small>{progress}% uploaded</small>
              </div>
            )}
            {uploadResult && (
              <div className="upload-success">
                <FontAwesomeIcon icon={faCheck} />
                <span>
                  <strong>Version {uploadResult.version} is active</strong>
                  {uploadResult.recordCount} records imported successfully.
                </span>
              </div>
            )}
          </div>
        </section>
      )}
      {activeTab === "Records" && (
        <section className="panel table-panel">
          <div className="toolbar records-toolbar">
            <label className="search-box">
              <FontAwesomeIcon icon={faSearch} />
              <input
                placeholder="Search titles and content…"
                aria-label="Search knowledge records"
                value={query.search}
                onChange={(e) =>
                  setQuery({ ...query, search: e.target.value, page: 1 })
                }
              />
            </label>
            <select
              aria-label="Category"
              value={query.category}
              onChange={(e) =>
                setQuery({ ...query, category: e.target.value, page: 1 })
              }
            >
              <option value="">All categories</option>
              {stats.categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            <select
              aria-label="Record status"
              value={query.active}
              onChange={(e) =>
                setQuery({ ...query, active: e.target.value, page: 1 })
              }
            >
              <option value="">All records</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            {selected.length > 0 && (
              <Button
                variant="danger"
                icon={faTrash}
                onClick={() => setConfirm({ type: "bulk" })}
              >
                Delete {selected.length}
              </Button>
            )}
          </div>
          {!records.items.length ? (
            <EmptyState
              title="No records found"
              message="Try another filter or upload a knowledge file."
            />
          ) : (
            <>
              <div className="data-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          aria-label="Select all records"
                          checked={
                            selected.length === records.items.length &&
                            !!records.items.length
                          }
                          onChange={(e) =>
                            setSelected(
                              e.target.checked
                                ? records.items.map((r) => r._id)
                                : [],
                            )
                          }
                        />
                      </th>
                      <th>Record</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Version</th>
                      <th>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.items.map((record) => (
                      <tr key={record._id}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${record.title}`}
                            checked={selected.includes(record._id)}
                            onChange={(e) =>
                              setSelected(
                                e.target.checked
                                  ? [...selected, record._id]
                                  : selected.filter((id) => id !== record._id),
                              )
                            }
                          />
                        </td>
                        <td data-label="Record">
                          <strong>{record.title}</strong>
                          <small className="truncate-text">
                            {record.content}
                          </small>
                        </td>
                        <td data-label="Category">
                          <span className="category-chip">
                            {record.category}
                          </span>
                        </td>
                        <td data-label="Status">
                          <button
                            className="status-toggle"
                            onClick={() => toggle(record)}
                            aria-label={`${record.isActive ? "Disable" : "Enable"} ${record.title}`}
                          >
                            <StatusBadge active={record.isActive} />
                          </button>
                        </td>
                        <td data-label="Version">v{record.version}</td>
                        <td className="row-actions">
                          <div>
                            <button
                              onClick={() => setViewing(record)}
                              title="View"
                              aria-label={`View ${record.title}`}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button
                              onClick={() => setEditing({ ...record })}
                              title="Edit"
                              aria-label={`Edit ${record.title}`}
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </button>
                            <button
                              className="danger-icon"
                              onClick={() =>
                                setConfirm({ type: "delete", record })
                              }
                              title="Delete"
                              aria-label={`Delete ${record.title}`}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                {...records.pagination}
                onChange={(page) => setQuery({ ...query, page })}
              />
            </>
          )}
        </section>
      )}
      {activeTab === "Categories" && (
        <section className="category-grid">
          {categoryRows.length ? (
            categoryRows.map((item, index) => (
              <article className="panel" key={item.category}>
                <span className={`category-index tone-${index % 4}`}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3>{item.category}</h3>
                <p>Searchable records in the active knowledge version.</p>
              </article>
            ))
          ) : (
            <EmptyState
              title="No categories yet"
              message="Categories are derived automatically from uploaded records."
            />
          )}
        </section>
      )}
      {(activeTab === "Versions" || activeTab === "Import History") && (
        <section className="panel table-panel">
          {!versions.length ? (
            <EmptyState
              title="No import history"
              message="Your completed and failed uploads will appear here."
            />
          ) : (
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>File</th>
                    <th>Records</th>
                    <th>Status</th>
                    <th>Uploaded</th>
                    <th>Uploader</th>
                    {activeTab === "Versions" && <th />}
                  </tr>
                </thead>
                <tbody>
                  {versions.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <strong>v{item.version}</strong>
                        {item.version === stats.currentVersion && (
                          <small className="current-tag">Current</small>
                        )}
                      </td>
                      <td>
                        {item.filename}
                        <small>{fileSize(item.fileSize)}</small>
                      </td>
                      <td>{item.recordCount}</td>
                      <td>
                        <StatusBadge active={item.status !== "failed"}>
                          {item.status}
                        </StatusBadge>
                        {item.error && (
                          <small className="error-copy">{item.error}</small>
                        )}
                      </td>
                      <td>{formatDate(item.uploadedAt)}</td>
                      <td>{item.uploadedBy?.name || "Administrator"}</td>
                      {activeTab === "Versions" && (
                        <td className="row-actions">
                          {item.version !== stats.currentVersion &&
                            item.status !== "failed" && (
                              <Button
                                variant="secondary"
                                icon={faArrowRotateLeft}
                                onClick={() =>
                                  setConfirm({
                                    type: "rollback",
                                    version: item.version,
                                  })
                                }
                              >
                                Rollback
                              </Button>
                            )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
      <Modal
        open={!!viewing}
        title={viewing?.title || "Knowledge record"}
        onClose={() => setViewing(null)}
        wide
      >
        <div className="record-view">
          <div>
            <span>Category</span>
            <strong>{viewing?.category}</strong>
          </div>
          <div>
            <span>Source</span>
            <strong>{viewing?.source || "Uploaded JSON"}</strong>
          </div>
          <pre>{viewing?.content}</pre>
        </div>
      </Modal>
      <Modal
        open={!!editing}
        title="Edit knowledge record"
        onClose={() => setEditing(null)}
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={saveRecord} disabled={busy}>
              Save record
            </Button>
          </>
        }
      >
        <form className="form-grid" onSubmit={saveRecord}>
          {editing && (
            <>
              <Field label="Title">
                <input
                  required
                  value={editing.title}
                  onChange={(e) =>
                    setEditing({ ...editing, title: e.target.value })
                  }
                />
              </Field>
              <Field label="Category">
                <input
                  required
                  value={editing.category}
                  onChange={(e) =>
                    setEditing({ ...editing, category: e.target.value })
                  }
                />
              </Field>
              <Field className="field-full" label="Content">
                <textarea
                  required
                  rows="10"
                  value={editing.content}
                  onChange={(e) =>
                    setEditing({ ...editing, content: e.target.value })
                  }
                />
              </Field>
              <label className="toggle-field field-full">
                <input
                  type="checkbox"
                  checked={editing.isActive}
                  onChange={(e) =>
                    setEditing({ ...editing, isActive: e.target.checked })
                  }
                />
                <span />
                <div>
                  <strong>Record active</strong>
                  <small>
                    Inactive records are excluded from visitor retrieval.
                  </small>
                </div>
              </label>
            </>
          )}
        </form>
      </Modal>
      <ConfirmModal
        open={!!confirm}
        title={
          confirm?.type === "rollback"
            ? "Rollback knowledge version"
            : "Delete knowledge"
        }
        message={
          confirm?.type === "rollback"
            ? `Make version ${confirm?.version} the active knowledge set? Current records remain stored for future rollback.`
            : confirm?.type === "bulk"
              ? `Permanently delete ${selected.length} selected records?`
              : `Permanently delete “${confirm?.record?.title}”?`
        }
        confirmLabel={
          confirm?.type === "rollback" ? "Rollback version" : "Delete"
        }
        danger={confirm?.type !== "rollback"}
        busy={busy}
        onConfirm={executeConfirm}
        onClose={() => setConfirm(null)}
      />
    </>
  );
}
